import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ParagraphText } from '../StyledComponents/Typography';
import {
  BaseTableRow,
  SelectedTableHeaderRow,
  SelectedTableRow,
} from '../StyledComponents/Table';
import styled from 'styled-components';
import { withTheme } from '@canopysecurity/component-library';

import {
  Artifact,
  DeploymentAttempt,
  useGetDeploymentQuery,
  useGetDeviceDeploymentsQuery,
} from '../gql/generated';
import CondorSpinner from '../Components/CondorSpinner';
import {
  CardContainer,
  CardKey,
  CardTitle,
  CardValue,
} from '../StyledComponents/Card';
import ExportCsv from '../Components/ExportCsv';

export const SelectedDeploymentAttemptRow = styled(BaseTableRow)`
  grid-template-columns: 25% 35% 25% 15%;
  padding: 10px 0 10px 10px;
  border-radius: 0px;
`;

export const SelectedDeploymentAttemptHeaderRow = styled(
  SelectedDeploymentAttemptRow,
)`
  background-color: unset;
`;

interface TableRowProps {
  shadowedBackground: boolean;
}

const IncludedArtifactsTable = withTheme(styled(BaseTableRow)`
  gap: 0;
  margin-top: unset;
  padding: 0 10px 10px 10px;
  grid-template-columns: 33fr 33fr 34fr;
  background-color: ${({ theme }) => theme.background.main};
  border: 1px solid ${({ theme }) => theme.text.main};
  margin-top: 20px;

  > * {
    border-bottom: 1px solid ${({ theme }) => theme.text.main};
  }
`);

const DeploymentAttemptsTable = withTheme(styled(BaseTableRow)`
  gap: 0;
  margin-top: unset;
  padding: 0 10px 10px 10px;
  grid-template-columns: 25fr 35fr 25fr 15fr;
  background-color: ${({ theme }) => theme.background.main};
  border: 1px solid ${({ theme }) => theme.text.main};
  margin-top: 20px;

  > * {
    border-bottom: 1px solid ${({ theme }) => theme.text.main};
  }
`);

export const SelectedDeploymentAttemptsTableRow = styled(BaseTableRow)`
  grid-template-columns: 25% 35% 25% 15%;
  padding: 10px 0 10px 10px;
  border-radius: 0px;
`;

const DeploymentAttemptsTableRow = styled(
  SelectedDeploymentAttemptsTableRow,
)<TableRowProps>`
  ${({ shadowedBackground }) =>
    shadowedBackground &&
    `
    background-color: #00000000;
  `}
`;

const IncludedArtifactsTableRow = styled(SelectedTableRow)<TableRowProps>`
  ${({ shadowedBackground }) =>
    shadowedBackground &&
    `
    background-color: #00000000;
  `}
`;

const TableHeader = styled.span`
  font-size: 12px;
  font-weight: bolder;
`;

// Execution Overview Styled Components
const OverviewContainer = styled.div`
  /* No border or background - CardContainer handles that */
`;

const OverviewTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const OverviewSubtitle = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0 0 24px 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px 48px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: #666;
  text-transform: capitalize;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 600;
  color: #0066cc;
`;

export const DeploymentDetails = (): JSX.Element => {
  const { id } = useParams();
  const { data, loading: deploymentLoading } = useGetDeploymentQuery({
    variables: { id: id ?? '' },
  });
  const { data: deviceDeploymentData, loading: deviceDeploymentLoading } =
    useGetDeviceDeploymentsQuery({
      variables: { deploymentId: id ?? '' },
    });

  const [deployment, setDeployment] = useState<any>();
  const [executionStats, setExecutionStats] = useState<any>();

  useEffect(() => {
    const date = new Date(data?.getDeployment?.createTime ?? Date.now());
    const rawAttempts = (data?.getDeployment?.attempts ?? []).filter(
      Boolean,
    ) as DeploymentAttempt[];
    const attempts = rawAttempts.map((attempt) => {
      return {
        startTime: `${new Date(
          attempt.startTime ?? Date.now(),
        ).toLocaleDateString()} ${new Date(
          attempt.startTime ?? Date.now(),
        ).toLocaleTimeString()}`,
        deployedBy: attempt.deployedBy ?? 'unknown',
        tags: attempt.tags ?? ['unknown'],
        forceInstall: attempt.forceInstall ?? false,
      };
    });
    setDeployment({
      version: data?.getDeployment?.release?.semver ?? '?',
      jobId: data?.getDeployment?.jobId ?? '-',
      releaseDate: date.toLocaleDateString(),
      releaseTime: date.toLocaleTimeString(),
      consoleUrl:
        data?.getDeployment?.consoleJobUrl ??
        'https://us-east-1.console.aws.amazon.com/',
      attempts: attempts,
      tags: data?.getDeployment?.tags ?? [],
    });
  }, [data]);

  useEffect(() => {
    const rawDeviceDeployments =
      deviceDeploymentData?.getDeviceDeployments?.items;

    const deviceDeploymentsWorking = rawDeviceDeployments?.map(
      (deviceDeployment) => ({
        id: deviceDeployment?.id ?? '',
        deploymentId: deviceDeployment?.deploymentId ?? '',
        status: deviceDeployment?.status ?? '',
        canopyDeviceId: deviceDeployment?.canopyDeviceId ?? 'unknown',
        serialNumber: deviceDeployment?.serialNumber ?? 'unknown',
        downloadStatus: deviceDeployment?.downloadStatus ?? 'unknown',
        installStatus: deviceDeployment?.installStatus ?? 'unknown',
        states: {
          download: deviceDeployment?.states?.download ?? [],
          install: deviceDeployment?.states?.install ?? [],
        },
      }),
    );

    // Count overall deployment status
    const statusCounts = {
      succeeded:
        deviceDeploymentsWorking?.filter(
          (d) => d.status === 'COMPLETE' || d.status == 'SUCCEEDED',
        ).length || 0,
      active:
        deviceDeploymentsWorking?.filter((d) => d.status === 'ACTIVE').length ||
        0,
      failed:
        deviceDeploymentsWorking?.filter((d) => d.status === 'FAILED').length ||
        0,
      abandoned:
        deviceDeploymentsWorking?.filter((d) => d.status === 'ABANDONED')
          .length || 0,
    };

    // Count download execution states
    const downloadStats = {
      succeeded:
        deviceDeploymentsWorking?.filter(
          (d) => d.downloadStatus === 'SUCCEEDED',
        ).length || 0,
      failed:
        deviceDeploymentsWorking?.filter((d) => d.downloadStatus === 'FAILED')
          .length || 0,
      canceled:
        deviceDeploymentsWorking?.filter(
          (d) =>
            d.downloadStatus === 'CANCELED' || d.downloadStatus === 'CANCELLED',
        ).length || 0,
      rejected:
        deviceDeploymentsWorking?.filter((d) => d.downloadStatus === 'REJECTED')
          .length || 0,
      queued:
        deviceDeploymentsWorking?.filter((d) => d.downloadStatus === 'QUEUED')
          .length || 0,
      inProgress:
        deviceDeploymentsWorking?.filter(
          (d) => d.downloadStatus === 'IN_PROGRESS',
        ).length || 0,
      removed:
        deviceDeploymentsWorking?.filter((d) => d.downloadStatus === 'REMOVED')
          .length || 0,
      timedOut:
        deviceDeploymentsWorking?.filter(
          (d) => d.downloadStatus === 'TIMED_OUT',
        ).length || 0,
    };

    // Count install execution states
    const installStats = {
      succeeded:
        deviceDeploymentsWorking?.filter((d) => d.installStatus === 'SUCCEEDED')
          .length || 0,
      failed:
        deviceDeploymentsWorking?.filter((d) => d.installStatus === 'FAILED')
          .length || 0,
      canceled:
        deviceDeploymentsWorking?.filter(
          (d) =>
            d.installStatus === 'CANCELED' || d.installStatus === 'CANCELLED',
        ).length || 0,
      rejected:
        deviceDeploymentsWorking?.filter((d) => d.installStatus === 'REJECTED')
          .length || 0,
      queued:
        deviceDeploymentsWorking?.filter(
          (d) =>
            d.installStatus === 'QUEUED' || d.installStatus === 'REQUESTED',
        ).length || 0,
      inProgress:
        deviceDeploymentsWorking?.filter(
          (d) => d.installStatus === 'IN_PROGRESS',
        ).length || 0,
      removed:
        deviceDeploymentsWorking?.filter((d) => d.installStatus === 'REMOVED')
          .length || 0,
      timedOut:
        deviceDeploymentsWorking?.filter((d) => d.installStatus === 'TIMED_OUT')
          .length || 0,
    };

    setExecutionStats({
      status: statusCounts,
      download: downloadStats,
      install: installStats,
    });
  }, [data, deviceDeploymentData]);

  const artifacts = useMemo(
    () =>
      (data?.getDeployment?.release?.artifacts ?? []).filter(
        (a): a is Artifact => !!a,
      ),
    [data?.getDeployment?.release?.artifacts],
  );

  return (
    <>
      <div style={{ display: 'flex' }}>
        <div style={{ flexBasis: '50%' }}>
          <CardContainer>
            <CardTitle>Deployment Details</CardTitle>
            {deploymentLoading ? (
              <CondorSpinner />
            ) : (
              <div>
                <div style={{ display: 'flex' }}>
                  <div style={{ flexBasis: '40%' }}>
                    <CardKey>JOB ID</CardKey>
                    <CardKey>RELEASE VERSION</CardKey>
                    <CardKey>RELEASE DATE</CardKey>
                    <CardKey>RELEASE TIME</CardKey>
                    <CardKey>CURRENT TAG(S)</CardKey>
                  </div>
                  <div style={{ flexBasis: '60%' }}>
                    <CardValue>{deployment?.jobId}</CardValue>
                    <CardValue>{deployment?.version}</CardValue>
                    <CardValue>{deployment?.releaseDate}</CardValue>
                    <CardValue>{deployment?.releaseTime}</CardValue>
                    <CardValue>
                      {deployment?.tags.map((tag: string) => `${tag} `)}
                    </CardValue>
                  </div>
                </div>
                <CardValue>
                  Click the{' '}
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    to={deployment?.consoleUrl}
                  >
                    Link
                  </Link>{' '}
                  to view progress in IoT Core console (Download only)
                </CardValue>
              </div>
            )}
          </CardContainer>
          <CardContainer>
            <CardTitle>Artifacts Included</CardTitle>
            {deploymentLoading ? (
              <CondorSpinner />
            ) : (
              <div>
                <IncludedArtifactsTable>
                  <SelectedTableHeaderRow>
                    <TableHeader>ARTIFACT CATEGORY</TableHeader>
                    <TableHeader>ARTIFACT NAME</TableHeader>
                  </SelectedTableHeaderRow>
                  {artifacts.map((artifact, index: number) => {
                    return (
                      <IncludedArtifactsTableRow
                        key={artifact.typeName}
                        shadowedBackground={index % 2 === 1}
                      >
                        <ParagraphText>{artifact.typeName}</ParagraphText>
                        <ParagraphText>
                          {artifact.typeName}-{artifact.semver}
                        </ParagraphText>
                      </IncludedArtifactsTableRow>
                    );
                  }) || <></>}
                </IncludedArtifactsTable>
              </div>
            )}
          </CardContainer>
          <CardContainer>
            <CardTitle>Deployment Attempts</CardTitle>
            {deploymentLoading ? (
              <CondorSpinner />
            ) : (
              <div>
                <DeploymentAttemptsTable>
                  <SelectedDeploymentAttemptHeaderRow>
                    <TableHeader>STARTED</TableHeader>
                    <TableHeader>BY</TableHeader>
                    <TableHeader>TO</TableHeader>
                    <TableHeader>FORCE INSTALL</TableHeader>
                  </SelectedDeploymentAttemptHeaderRow>
                  {deployment?.attempts?.map(
                    (deploymentAttempt: any, index: number) => {
                      return (
                        <DeploymentAttemptsTableRow
                          key={deploymentAttempt.startTime}
                          shadowedBackground={index % 2 === 1}
                        >
                          <ParagraphText>
                            {deploymentAttempt.startTime}
                          </ParagraphText>
                          <ParagraphText>
                            {deploymentAttempt.deployedBy}
                          </ParagraphText>
                          <ParagraphText>
                            {deploymentAttempt.tags.map(
                              (tag: any) => `${tag} `,
                            )}
                          </ParagraphText>
                          <ParagraphText>
                            {deploymentAttempt.forceInstall ? 'Yes' : 'No'}
                          </ParagraphText>
                        </DeploymentAttemptsTableRow>
                      );
                    },
                  ) || <></>}
                </DeploymentAttemptsTable>
              </div>
            )}
          </CardContainer>
        </div>
        <div style={{ flexBasis: '50%' }}>
          {deviceDeploymentLoading ? (
            <CardContainer>
              <CondorSpinner />
            </CardContainer>
          ) : (
            <>
              {/* Total Deployments */}
              <CardContainer>
                <OverviewContainer>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <OverviewTitle>
                      Total Deployments:{' '}
                      {
                        deviceDeploymentData?.getDeviceDeployments?.items
                          ?.length
                      }
                    </OverviewTitle>
                    <div>
                      {deviceDeploymentData?.getDeviceDeployments?.items &&
                        deviceDeploymentData.getDeviceDeployments.items.length >
                          0 && (
                          <ExportCsv
                            data={deviceDeploymentData.getDeviceDeployments.items
                              .filter(Boolean)
                              .map((item) => ({
                                id: item?.id,
                                deploymentId: item?.deploymentId,
                                canopyDeviceId: item?.canopyDeviceId,
                                serialNumber: item?.serialNumber,
                                status: item?.status,
                                downloadStatus: item?.downloadStatus,
                                installStatus: item?.installStatus ?? 'N/A',
                                updatedAt: item?.updatedAt,
                              }))}
                            filename={`device-deployments-${
                              deployment?.jobId
                            }-${new Date().toLocaleDateString()}.csv`}
                          />
                        )}
                    </div>
                  </div>
                </OverviewContainer>
              </CardContainer>

              {executionStats && (
                <>
                  {/* Overall Status */}
                  <CardContainer>
                    <OverviewContainer>
                      <OverviewTitle>Deployment Status</OverviewTitle>
                      <OverviewSubtitle>
                        Overall status of device deployments.
                      </OverviewSubtitle>

                      <StatsGrid
                        style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}
                      >
                        <StatItem>
                          <StatLabel>Succeeded</StatLabel>
                          <StatValue>
                            {executionStats.status?.succeeded}
                          </StatValue>
                        </StatItem>

                        <StatItem>
                          <StatLabel>Active</StatLabel>
                          <StatValue>{executionStats.status?.active}</StatValue>
                        </StatItem>

                        <StatItem>
                          <StatLabel>Failed</StatLabel>
                          <StatValue>{executionStats.status?.failed}</StatValue>
                        </StatItem>

                        <StatItem>
                          <StatLabel>Abandoned</StatLabel>
                          <StatValue>
                            {executionStats.status?.abandoned}
                          </StatValue>
                        </StatItem>
                      </StatsGrid>
                    </OverviewContainer>
                  </CardContainer>

                  {/* Download Execution Overview */}
                  <CardContainer>
                    <OverviewContainer>
                      <OverviewTitle>Download Execution overview</OverviewTitle>
                      <OverviewSubtitle>
                        When canceling and deleting execution refresh this page
                        if the summary does not match.
                      </OverviewSubtitle>

                      <StatsGrid>
                        <StatItem>
                          <StatLabel>Succeeded</StatLabel>
                          <StatValue>
                            {executionStats.download?.succeeded}
                          </StatValue>
                        </StatItem>

                        <StatItem>
                          <StatLabel>Failed</StatLabel>
                          <StatValue>
                            {executionStats.download?.failed}
                          </StatValue>
                        </StatItem>

                        <StatItem>
                          <StatLabel>Canceled</StatLabel>
                          <StatValue>
                            {executionStats.download?.canceled}
                          </StatValue>
                        </StatItem>

                        <StatItem>
                          <StatLabel>Rejected</StatLabel>
                          <StatValue>
                            {executionStats.download?.rejected}
                          </StatValue>
                        </StatItem>

                        <StatItem>
                          <StatLabel>Queued</StatLabel>
                          <StatValue>
                            {executionStats.download?.queued}
                          </StatValue>
                        </StatItem>

                        <StatItem>
                          <StatLabel>In progress</StatLabel>
                          <StatValue>
                            {executionStats.download?.inProgress}
                          </StatValue>
                        </StatItem>

                        <StatItem>
                          <StatLabel>Removed</StatLabel>
                          <StatValue>
                            {executionStats.download?.removed}
                          </StatValue>
                        </StatItem>

                        <StatItem>
                          <StatLabel>Timed out</StatLabel>
                          <StatValue>
                            {executionStats.download?.timedOut}
                          </StatValue>
                        </StatItem>
                      </StatsGrid>
                    </OverviewContainer>
                  </CardContainer>

                  {/* Install Execution Overview */}
                  <CardContainer>
                    <OverviewContainer>
                      <OverviewTitle>Install Execution overview</OverviewTitle>
                      <OverviewSubtitle>
                        When canceling and deleting execution refresh this page
                        if the summary does not match.
                      </OverviewSubtitle>

                      <StatsGrid>
                        <StatItem>
                          <StatLabel>Succeeded</StatLabel>
                          <StatValue>
                            {executionStats.install?.succeeded}
                          </StatValue>
                        </StatItem>

                        <StatItem>
                          <StatLabel>Failed</StatLabel>
                          <StatValue>
                            {executionStats.install?.failed}
                          </StatValue>
                        </StatItem>

                        <StatItem>
                          <StatLabel>Canceled</StatLabel>
                          <StatValue>
                            {executionStats.install?.canceled}
                          </StatValue>
                        </StatItem>

                        <StatItem>
                          <StatLabel>Rejected</StatLabel>
                          <StatValue>
                            {executionStats.install?.rejected}
                          </StatValue>
                        </StatItem>

                        <StatItem>
                          <StatLabel>Queued</StatLabel>
                          <StatValue>
                            {executionStats.install?.queued}
                          </StatValue>
                        </StatItem>

                        <StatItem>
                          <StatLabel>In progress</StatLabel>
                          <StatValue>
                            {executionStats.install?.inProgress}
                          </StatValue>
                        </StatItem>

                        <StatItem>
                          <StatLabel>Removed</StatLabel>
                          <StatValue>
                            {executionStats.install?.removed}
                          </StatValue>
                        </StatItem>

                        <StatItem>
                          <StatLabel>Timed out</StatLabel>
                          <StatValue>
                            {executionStats.install?.timedOut}
                          </StatValue>
                        </StatItem>
                      </StatsGrid>
                    </OverviewContainer>
                  </CardContainer>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
